import { useState, useEffect, useCallback, useRef, lazy, Suspense } from "react";
import { useTranslation } from "react-i18next";
import WebApp from "@twa-dev/sdk";
import { User, WsEvent } from "./types/game";
import { useGameSession } from "./hooks/useGameSession";
import { useTournament } from "./hooks/useTournament";
import { useLocalGame } from "./hooks/useLocalGame";
import LobbyView from "./components/lobby/LobbyView";
import GameView from "./components/game/GameView";
import SetupView from "./components/setup/SetupView";

// Lazy-loaded: LoginView drags in the whole firebase SDK, the tournament and
// stats screens are off the hot path — keep them out of the initial bundle.
const LoginView = lazy(() => import("./components/auth/LoginView"));
const StatsView = lazy(() => import("./components/stats/StatsView"));
const TournamentLobby = lazy(() => import("./components/game/TournamentLobby"));
const TournamentFinishedScreen = lazy(() => import("./components/game/TournamentFinishedScreen"));
const CreateTournamentModal = lazy(() => import("./components/setup/CreateTournamentModal"));

const API_URL = import.meta.env.VITE_API_URL || "/api/v1";

const fullScreenSpinner = (
  <div className="min-h-screen flex items-center justify-center">
    <div className="w-8 h-8 border-4 border-blue-500/20 border-t-blue-500 rounded-full animate-spin" />
  </div>
);

function App() {
  const { t } = useTranslation();

  useEffect(() => {
    document.title = t("app.title");
  }, [t]);

  const [token, setToken] = useState<string | null>(localStorage.getItem("token"));
  const [me, setMe] = useState<User | null>(null);
  const [telegramAuthFailed, setTelegramAuthFailed] = useState(false);
  const [view, setView] = useState<"lobby" | "setup" | "game" | "stats" | "tournament-lobby" | "tournament-standings" | "tournament-finished">("lobby");
  const [isLocalMode, setIsLocalMode] = useState<boolean>(() => {
    return localStorage.getItem("poker_dice_mode") === "local";
  });
  const [showCreateTournament, setShowCreateTournament] = useState(false);
  const [latestEvent, setLatestEvent] = useState<WsEvent | null>(null);
  const hasAutoJoined = useRef(false);
  // Tracks when user navigates to a historical game from TournamentFinishedScreen so the
  // navigation effect doesn't immediately redirect them back to the finished screen.
  const viewingMatchFromFinished = useRef(false);

  // Tournament hook
  const tournamentHook = useTournament(token, (_sessionId, inviteCode) => {
    // When next game is created in tournament
    onlineGame.joinByCode(inviteCode);
  });

  const {
    tournament,
    matrix,
    loading: tournamentLoading,
    error: tournamentError,
    fetchTournamentByCode,
    createTournament,
    joinTournament,
    addRealParticipant,
    addPhantomParticipant,
    removeParticipant,
    fetchMatrix,
    startNextGame,
    finishTournament,
    setTournament,
    setMatrix,
  } = tournamentHook;

  // Persistence for mode
  useEffect(() => {
    localStorage.setItem("poker_dice_mode", isLocalMode ? "local" : "online");
  }, [isLocalMode]);

  const loginFirebase = async (idToken: string) => {
    const res = await fetch(`${API_URL}/auth/login/firebase`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id_token: idToken }),
    });
    if (!res.ok) throw new Error("Sign-in failed");
    const data = await res.json();
    localStorage.setItem("token", data.access_token);
    setToken(data.access_token);
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("poker_dice_mode");
    setToken(null);
    setMe(null);
  };

  const loginGuest = async () => {
    const res = await fetch(`${API_URL}/auth/login/guest`, { method: "POST" });
    if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`Guest login failed (${res.status}): ${text.slice(0, 50)}`);
    }
    const data = await res.json();
    localStorage.setItem("token", data.access_token);
    setToken(data.access_token);
  };

  const telegramLogin = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/auth/login/telegram`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ init_data: WebApp.initData }),
      });
      if (!res.ok) {
        setTelegramAuthFailed(true);
        return;
      }
      const data = await res.json();
      localStorage.setItem("token", data.access_token);
      setToken(data.access_token);
      setTelegramAuthFailed(false);
    } catch {
      setTelegramAuthFailed(true);
    }
  }, []);

  const fetchMe = useCallback(async (authToken: string) => {
    try {
      const res = await fetch(`${API_URL}/auth/users/me`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (res.status === 401 || res.status === 403) {
        // Token is genuinely invalid — drop it so the user re-authenticates
        localStorage.removeItem("token");
        setToken(null);
        return;
      }
      if (!res.ok) throw new Error();
      const data = await res.json();
      setMe(data);
    } catch {
      // Network hiccup — keep the token and retry instead of logging out.
      // Dropping the token here would mint a fresh guest identity and orphan
      // the user's games ("Joined as guest" / lost admin rights).
      setTimeout(() => fetchMe(authToken), 3000);
    }
  }, []);

  const onlineGame = useGameSession(token || "", (ev) => setLatestEvent(ev));
  const localGame = useLocalGame(me);

  const game = isLocalMode ? localGame : onlineGame;
  const {
    state,
    error,
    loading,
    createSession,
    fetchById,
    addRealPlayer,
    addPhantomPlayer,
    removePlayer,
    updateScore,
    updateSettings,
    finishGame,
    startGame,
    rematchGame,
    rollDice,
    toggleHold,
    sendReaction,
    notifyResults,
    reorderPlayers,
    updatePlayerName,
    leaveGame,
    wsStatus,
    queuedCount,
    suggestions,
    } = game;

  // ── Telegram init ──────────────────────────────────────────────────────────
  useEffect(() => {
    WebApp.ready();
    WebApp.expand();
    if (WebApp.initData?.trim() && !token) telegramLogin();
  }, [telegramLogin, token]);

  // Inside Telegram, a stale anonymous-guest token from a previous session can
  // shadow the real Telegram identity (user shows up as guest/spectator in their
  // own games). If the loaded account has no Telegram id and no email, upgrade
  // it to the Telegram identity.
  useEffect(() => {
    if (WebApp.initData?.trim() && me && !me.telegram_id && !me.email) {
      telegramLogin();
    }
  }, [me, telegramLogin]);

  useEffect(() => {
    if (token) fetchMe(token);
    
    // Auto-join via deep link or browser URL parameter
    if (token && !state && !tournament && !hasAutoJoined.current && !isLocalMode) {
      const params = new URLSearchParams(window.location.search);
      const urlCode = params.get("code") || params.get("startapp") || WebApp.initDataUnsafe?.start_param;
      if (urlCode) {
        hasAutoJoined.current = true;
        const cleanUrl = window.location.pathname;
        window.history.replaceState({}, document.title, cleanUrl);
        
        // Try as game first, then as tournament
        onlineGame.joinByCode(urlCode).catch(() => {
          fetchTournamentByCode(urlCode);
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, state, tournament, onlineGame.joinByCode, fetchTournamentByCode, fetchMe, isLocalMode]);

  // Auto-fetch matrix on tournament changes (including when it finishes)
  useEffect(() => {
    if (tournament && token) {
      fetchMatrix();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tournament?.id, tournament?.current_game_index, tournament?.status, token, fetchMatrix]);
  const handleLeaveTournament = useCallback(() => {
    setTournament(null);
    setMatrix(null);
    setView("lobby");
  }, [setTournament, setMatrix, setView]);

  // ── Navigate on session/tournament change ─────────────────────────────────────────────
  const hasState = !!state;
  useEffect(() => {
    // If we are explicitly in "stats" view, don't let state override it
    if (view === "stats") return;

    // If we are in the middle of loading a session, wait
    if (loading || tournamentLoading) return;

    // 1. If the tournament is finished, show the ceremony screen —
    //    unless the user explicitly navigated to a historical match from that screen.
    if (tournament?.status === "finished" && !viewingMatchFromFinished.current) {
      setView("tournament-finished");
      return;
    }

    // 2. If we have an active game state, we should be in 'game' or 'setup'
    if (state) {
      if (state.session.status === "finished" || state.session.status === "active") {
        setView("game");
      } else {
        setView("setup");
      }
      return;
    }

    // 3. If no game state but we have a tournament, we are in the lobby
    if (tournament) {
      setView("tournament-lobby");
      return;
    }

    // 3. Fallback to main lobby
    setView("lobby");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.session.id, state?.session.status, hasState, tournament?.id, tournament?.status, loading, tournamentLoading, view]);

  if (!token || (token && !me)) {
    if (WebApp.initData?.trim()) {
      if (telegramAuthFailed && !token) {
        return (
          <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-6 text-center">
            <div className="text-5xl select-none">🎲</div>
            <p className="text-ink/70 text-sm">{t("auth.telegramFailed")}</p>
            <button
              onClick={() => {
                setTelegramAuthFailed(false);
                telegramLogin();
              }}
              className="px-8 py-3 rounded-xl font-bold bg-blue-600 hover:bg-blue-500 text-white transition-colors active:scale-95"
            >
              {t("auth.retry")}
            </button>
          </div>
        );
      }
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="w-8 h-8 border-4 border-blue-500/20 border-t-blue-500 rounded-full animate-spin" />
        </div>
      );
    }
    if (!token) {
        return (
            <Suspense fallback={fullScreenSpinner}>
                <LoginView
                    onLoginFirebase={loginFirebase}
                    onLoginGuest={loginGuest}
                />
            </Suspense>
        );
    }
    // Loading 'me'
    return (
        <div className="min-h-screen flex items-center justify-center">
            <div className="w-8 h-8 border-4 border-blue-500/20 border-t-blue-500 rounded-full animate-spin" />
        </div>
    );
  }

  const activeError = error || tournamentError;

  const errorBanner = activeError && (
    <div className="fixed top-0 left-0 right-0 z-50 bg-red-600 text-white text-center py-2 text-sm font-bold shadow-lg">
      {activeError}
    </div>
  );

  // Global Loading Spinner
  if (loading || tournamentLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-blue-500/20 border-t-blue-500 rounded-full animate-spin" />
      </div>
    );
  }

  const updateUser = async (data: Partial<User>) => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/auth/users/me`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });
      if (res.ok) {
        const updated = await res.json();
        setMe(updated);
      }
    } catch (err) {
      console.error("Failed to update user", err);
    }
  };

  const handleStartLocal = async () => {
    setIsLocalMode(true);
    await localGame.createSession();
  };

  const handleLeaveGame = () => {
    viewingMatchFromFinished.current = false;
    onlineGame.leaveGame();
    setIsLocalMode(false);

    if (tournament?.status === "finished") {
      setView("tournament-finished");
    } else if (tournament) {
      setView("tournament-lobby");
    } else {
      setView("lobby");
    }
  };

  // ── Tournament Finished ────────────────────────────────────────────────────
  if (view === "tournament-finished" && tournament)
    return (
      <Suspense fallback={fullScreenSpinner}>
        <TournamentFinishedScreen
          tournament={tournament}
          matrix={matrix}
          myUserId={me?.id}
          soundEnabled={me?.sound_enabled}
          onBack={() => {
            onlineGame.leaveGame();
            handleLeaveTournament();
          }}
          onViewMatch={async (id) => {
            viewingMatchFromFinished.current = true;
            setIsLocalMode(false);
            await onlineGame.fetchById(id);
            setView("game");
          }}
        />
      </Suspense>
    );

  // ── Stats ──────────────────────────────────────────────────────────────────
  if (view === "stats")
    return (
      <Suspense fallback={fullScreenSpinner}>
        <StatsView
          token={token}
          myTelegramId={me?.telegram_id ?? null}
          me={me!}
          onBack={() => setView("lobby")}
          onViewMatch={async (id) => {
            setIsLocalMode(false);
            await fetchById(id);
            setView("game");
          }}
        />
      </Suspense>
    );

  // ── Lobby ──────────────────────────────────────────────────────────────────
  if (view === "lobby" || (!state && !tournament))
    return (
      <>
        {errorBanner}
        <LobbyView
          token={token}
          onCreateGame={async () => {
            setIsLocalMode(false);
            await onlineGame.createSession();
          }}
          onStartLocal={handleStartLocal}
          onJoinGame={async (code) => {
            setIsLocalMode(false);
            try {
              await onlineGame.joinByCode(code);
            } catch {
              // Clear game session error before trying tournament
              onlineGame.setError(null);
              try {
                await fetchTournamentByCode(code);
              } catch {
                alert("Room not found — check the code");
              }
            }
          }}
          onJoinTournament={async (code) => {
            setIsLocalMode(false);
            await fetchTournamentByCode(code);
          }}
          onCreateTournament={() => setShowCreateTournament(true)}
          onViewMatch={async (id) => {
            setIsLocalMode(false);
            await onlineGame.fetchById(id);
          }}
          onWatchGame={async (id) => {
            setIsLocalMode(false);
            await onlineGame.fetchById(id);
          }}
          onShowStats={() => setView("stats")}
          onLogout={logout}
        />
        {showCreateTournament && (
          <Suspense fallback={null}>
            <CreateTournamentModal
              onClose={() => setShowCreateTournament(false)}
              loading={tournamentLoading}
              onCreate={async (name, games, gameMode, winnerMode) => {
                await createTournament(name, games, { game_mode: gameMode, winner_mode: winnerMode });
                setShowCreateTournament(false);
              }}
            />
          </Suspense>
        )}
      </>
    );

  const isAdmin = (state?.session.created_by || tournament?.created_by) === (me?.id || "local-admin");

  // ── Tournament Lobby ────────────────────────────────────────────────────────
  if (view === "tournament-lobby" && tournament) {
    const isJoined = tournament.participants.some(p => p.user_id === me?.id);
    return (
      <Suspense fallback={fullScreenSpinner}>
        <TournamentLobby
          tournament={tournament}
          matrix={matrix}
          isAdmin={isAdmin}
          token={token || ""}
          myUserId={me?.id}
          isJoined={isJoined}
          onJoin={joinTournament}
          onAddReal={addRealParticipant}
          onAddPhantom={addPhantomParticipant}
          onRemoveParticipant={removeParticipant}
          onViewMatch={async (id) => {
            setIsLocalMode(false);
            setView("game");
            await onlineGame.fetchById(id);
          }}
          onLeave={handleLeaveTournament}
          onStartNextGame={startNextGame}
          loading={tournamentLoading}
        />
      </Suspense>
    );
  }

  // ── Setup ──────────────────────────────────────────────────────────────────
  if (view === "setup" && state)
    return (
      <>
        {errorBanner}
        <SetupView
          state={state}
          token={token}
          myUserId={me?.id ?? "local-admin"}
          myTelegramId={me?.telegram_id ?? null}
          colorfulDice={me?.colorful_dice}
          onAddReal={addRealPlayer}
          onAddPhantom={addPhantomPlayer}
          onRemovePlayer={removePlayer}
          onUpdateSettings={updateSettings}
          onToggleColorful={(val) => updateUser({ colorful_dice: val })}
          onToggleSound={(val) => updateUser({ sound_enabled: val })}
          onToggleTheme={(val) => updateUser({ table_theme: val })}
          onStartGame={startGame}
          onReorderPlayers={reorderPlayers}
          onUpdatePlayerName={updatePlayerName}
          onLeave={handleLeaveGame}
          soundEnabled={me?.sound_enabled}
          themeId={me?.table_theme}
        />
      </>
    );

  // ── Game ───────────────────────────────────────────────────────────────────
  return (
    <>
      {errorBanner}
      <GameView
        state={state}
        myUserId={me?.id ?? "local-admin"}
        myTelegramId={me?.telegram_id ?? null}
        isAdmin={isAdmin}
        token={token}
        colorfulDice={me?.colorful_dice}
        soundEnabled={me?.sound_enabled}
        tableTheme={me?.table_theme}
        onUpdateScore={updateScore}
        onFinishGame={finishGame}
        wsStatus={wsStatus}
        queuedCount={queuedCount}
        suggestions={suggestions}
        latestEvent={latestEvent}
        onRollDice={rollDice}
        onToggleHold={toggleHold}
        onReact={sendReaction}
        onNotifyResults={notifyResults}
        onToggleColorful={(val) => updateUser({ colorful_dice: val })}
        onLeave={handleLeaveGame}
        tournament={tournament}
        tournamentMatrix={matrix}
        onShowTournamentStandings={fetchMatrix}
        onNextTournamentGame={startNextGame}
        onFinishTournament={finishTournament}
        onViewMatch={async (id) => {
          setIsLocalMode(false);
          setView("game");
          await onlineGame.fetchById(id);
        }}
        onCreateNewGame={() => {
          const wasLocal = isLocalMode;
          leaveGame();
          if (wasLocal) {
            handleStartLocal();
          } else {
            createSession();
          }
        }}
        onRematch={rematchGame}
        onUpdateSettings={updateSettings}
        onReorderPlayers={reorderPlayers}
        onRemovePlayer={removePlayer}
        onAddRealPlayer={addRealPlayer}
        onAddPhantomPlayer={addPhantomPlayer}
      />
    </>
  );
}

export default App;
